export type ParsedCsvEventRow = {
  name: string;
  city: string;
  event_date: string;
  venue_capacity: number;
  artist?: string;
  status: string;
};

export type CsvParseResult = {
  rows: ParsedCsvEventRow[];
  errors: string[];
};

const REQUIRED_HEADERS = ["name", "event_date", "city", "venue_capacity"] as const;

function splitCsvLine(line: string): string[] {
  const cells: string[] = [];
  let current = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }
    if (ch === "," && !inQuotes) {
      cells.push(current.trim());
      current = "";
      continue;
    }
    current += ch;
  }
  cells.push(current.trim());
  return cells;
}

function normalizeHeader(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, "_");
}

function isValidDateKey(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const [y, m, d] = value.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  return date.getFullYear() === y && date.getMonth() === m - 1 && date.getDate() === d;
}

export function parseEventsCsv(text: string): CsvParseResult {
  const errors: string[] = [];
  const lines = text
    .replace(/^\uFEFF/, "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  if (lines.length < 2) {
    return { rows: [], errors: ["CSV must include a header row and at least one data row."] };
  }

  const headers = splitCsvLine(lines[0]).map(normalizeHeader);
  const headerIndex = new Map(headers.map((header, index) => [header, index]));

  for (const required of REQUIRED_HEADERS) {
    if (!headerIndex.has(required)) {
      errors.push(`Missing required column: ${required}`);
    }
  }
  if (errors.length > 0) return { rows: [], errors };

  const rows: ParsedCsvEventRow[] = [];

  lines.slice(1).forEach((line, lineIndex) => {
    const cells = splitCsvLine(line);
    const rowNum = lineIndex + 2;
    const pick = (key: string) => cells[headerIndex.get(key) ?? -1]?.trim() ?? "";

    const name = pick("name");
    const city = pick("city");
    const event_date = pick("event_date");
    const capacityRaw = pick("venue_capacity");
    const artist = pick("artist") || undefined;
    const status = pick("status") || "draft";

    if (!name) {
      errors.push(`Row ${rowNum}: name is required`);
      return;
    }
    if (!city) {
      errors.push(`Row ${rowNum}: city is required`);
      return;
    }
    if (!isValidDateKey(event_date)) {
      errors.push(`Row ${rowNum}: event_date must be YYYY-MM-DD`);
      return;
    }
    const venue_capacity = Number(capacityRaw);
    if (!Number.isFinite(venue_capacity) || venue_capacity <= 0) {
      errors.push(`Row ${rowNum}: venue_capacity must be a positive number`);
      return;
    }

    rows.push({ name, city, event_date, venue_capacity, artist, status });
  });

  return { rows, errors };
}

export const EVENTS_CSV_TEMPLATE = `name,event_date,city,venue_capacity,artist,status
Summer Sessions,2026-08-15,Mumbai,1200,DJ Nova,draft
`;
